import { Component, computed, inject, signal, viewChild } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Router } from '@angular/router';
import { map } from 'rxjs';
import { UiEmptyStateComponent, UiLoadingComponent } from '@design-system';
import { CommodityAccessDeniedBottomSheetComponent } from '../../components/commodity-access-denied-bottom-sheet/commodity-access-denied-bottom-sheet.component';
import { KALA_WALLET_KEYS } from '../../models/wallet.model';
import { CommodityAccessService } from '../../services/commodity-access.service';
import { WalletStore } from './data-access/wallet.store';
import { WalletViewStateService } from './data-access/wallet-view-state.service';
import { WalletActionsComponent } from './components/wallet-actions/wallet-actions.component';
import { WalletBalanceStatusComponent } from './components/wallet-balance-status/wallet-balance-status.component';
import { OtherWalletsBottomSheetComponent } from './components/other-wallets-bottom-sheet/other-wallets-bottom-sheet.component';
import { WALLET_ACTION_ROUTES } from './domain/wallet-actions.constants';
import { WALLET_BALANCE_COLLAPSE_THRESHOLD, WALLET_BALANCE_SCROLL_DEAD_ZONE } from './domain/wallet-balance.constants';
import { WalletAction } from './domain/wallet-actions.model';
import { LastActiveTransfersComponent } from './components/last-active-transfers/last-active-transfers.component';
import { DepositPaymentResultComponent } from './deposit/components/deposit-payment-result/deposit-payment-result.component';
import { takeDepositPaymentResult } from './deposit/components/deposit-payment-result/deposit-payment-result.utils';

/**
 * تب کیف پول — موجودی کل across every wallet, the four money actions, and
 * «تراکنش‌های اخیر».
 */
@Component({
	selector: 'app-wallet',
	imports: [
		WalletBalanceStatusComponent,
		WalletActionsComponent,
		OtherWalletsBottomSheetComponent,
		LastActiveTransfersComponent,
		UiEmptyStateComponent,
		UiLoadingComponent,
		CommodityAccessDeniedBottomSheetComponent,
		DepositPaymentResultComponent
	],
	templateUrl: './wallet.component.html'
})
export class WalletComponent {
	private readonly walletStore = inject(WalletStore);
	private readonly viewState = inject(WalletViewStateService);
	private readonly router = inject(Router);
	private readonly commodityAccess = inject(CommodityAccessService);
	private readonly route = inject(ActivatedRoute);

	readonly accessDeniedSheet = viewChild<CommodityAccessDeniedBottomSheetComponent>('accessDeniedSheet');
	readonly otherWalletsSheet = viewChild<OtherWalletsBottomSheetComponent>('otherWalletsSheet');
	private readonly lastActiveTransfers = viewChild(LastActiveTransfersComponent);
	private readonly balanceStatus = viewChild(WalletBalanceStatusComponent);

	readonly amountsHidden = this.viewState.amountsHidden;

	/** The balance card's breakdown, folded away while the page is scrolled down. */
	readonly balanceCollapsed = signal(false);

	private previousScrollTop = 0;
	/** Where the current scroll up began; `null` while the list is not heading up. */
	private scrollUpFrom: number | null = null;

	/** A gateway deposit's outcome, when the bank's callback is what brought the trader here. */
	readonly paymentResult = toSignal(this.route.queryParamMap.pipe(map(takeDepositPaymentResult)), {
		initialValue: null
	});

	/** Whether there are read figures on the card at all, or only the store's opening zeros. */
	private readonly hasFigure = computed(() => KALA_WALLET_KEYS.some(key => this.walletStore.loaded()[key]));

	/**
	 * The loader only stands in on a cold load. The store is root-provided and keeps the
	 * last figures, so re-entering the tab shows them while the refresh is in flight
	 * rather than blanking the card.
	 */
	readonly showLoading = computed(() => this.walletStore.loading() && !this.hasFigure());

	/**
	 * A read was tried, failed, and left nothing on the card to fall back on. Not simply
	 * `error`: the store keeps the last figures through a failed refresh, and a dropped
	 * connection should not swap good money for «خطایی رخ داد!».
	 *
	 * A customer without a کد بورس کالا never reaches here: the store leaves the error
	 * null for the endpoint's refusal, and the card stands on its opening zeros.
	 */
	readonly readFailed = computed(() => !this.hasFigure() && this.walletStore.error() !== null);

	constructor() {
		this.reload();
	}

	onRetry(): void {
		this.reload();
	}

	/**
	 * Every money movement needs a کد بورس کالا, and is stopped at the tap — the sheet
	 * comes up in place of the flow rather than over it. The history stays open to all.
	 */
	onActionRequested(action: WalletAction): void {
		if (action !== 'history' && this.commodityAccess.denied()) {
			this.accessDeniedSheet()?.open();
			return;
		}

		this.router.navigate([WALLET_ACTION_ROUTES[action]]);
	}

	/** The bank's result is read; «تراکنش‌های اخیر» is re-read for where the payment now stands. */
	onPaymentResultDismissed(): void {
		this.lastActiveTransfers()?.reload();
	}

	onOtherWalletsRequested(): void {
		this.otherWalletsSheet()?.open();
	}

	/**
	 * Folds the breakdown as «تراکنش‌های اخیر» scrolls down and opens it on the way up, as
	 * the symbol page does its tabs — each only once the scroll has traveled the threshold
	 * that way. Folding hands its height to the list, so it only folds when the list still
	 * scrolls past the threshold without it; and the scroll a taller list clamps back at the
	 * bottom is not read as the way up — either would spring it straight open.
	 */
	onHistoryScroll(list: HTMLElement): void {
		const { scrollTop, scrollHeight, clientHeight } = list;
		const delta = scrollTop - this.previousScrollTop;
		if (Math.abs(delta) < WALLET_BALANCE_SCROLL_DEAD_ZONE && scrollTop > 0) {
			return;
		}
		const atBottom = scrollTop + clientHeight >= scrollHeight - 1;
		if (delta > 0) {
			this.scrollUpFrom = null;
			const overflowAfterFold = scrollHeight - clientHeight - (this.balanceStatus()?.breakdownHeight() ?? 0);
			if (
				scrollTop > WALLET_BALANCE_COLLAPSE_THRESHOLD &&
				overflowAfterFold > WALLET_BALANCE_COLLAPSE_THRESHOLD
			) {
				this.balanceCollapsed.set(true);
			}
		} else {
			// The clamp at the bottom is not the trader's: the climb is counted from where it left the list.
			this.scrollUpFrom = atBottom ? scrollTop : (this.scrollUpFrom ?? this.previousScrollTop);
			if (
				scrollTop <= WALLET_BALANCE_COLLAPSE_THRESHOLD ||
				this.scrollUpFrom - scrollTop > WALLET_BALANCE_COLLAPSE_THRESHOLD
			) {
				this.balanceCollapsed.set(false);
			}
		}
		this.previousScrollTop = scrollTop;
	}

	private reload(): void {
		// Fire-and-forget: the store reports its own failure through `error`.
		this.walletStore.load();
	}
}
